import styled, { css } from 'styled-components';
import { media } from '../../styles/theme';

export const Secao = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.espaco.md};

  & + & {
    margin-top: ${({ theme }) => theme.espaco.xl};
    padding-top: ${({ theme }) => theme.espaco.xl};
    border-top: 1px solid ${({ theme }) => theme.cores.border};
  }
`;

export const CabecalhoSecao = styled.header`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.espaco.md};

  h2 {
    flex: 1;
    min-width: 180px;
    font-size: ${({ theme }) => theme.fonte.h2};
    font-weight: 600;
    color: ${({ theme }) => theme.cores.ink};
  }
`;

// Atalhos de período e botão de filtros, numa linha acima dos gráficos
export const Controles = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.espaco.sm};

  ${media.mobile} {
    flex-basis: 100%;
  }
`;

export const Chips = styled.div`
  display: flex;
  min-width: 0;
  gap: ${({ theme }) => theme.espaco.xs};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  ${media.mobile} {
    flex: 1;
  }
`;

export const Chip = styled.button<{ $ativo: boolean }>`
  flex: 0 0 auto;
  height: ${({ theme }) => theme.alturaControle.sm};
  padding: 0 ${({ theme }) => theme.espaco.md};
  border: 1px solid ${({ $ativo, theme }) => ($ativo ? theme.cores.primary : theme.cores.border)};
  border-radius: ${({ theme }) => theme.raio.pill};
  background-color: ${({ $ativo, theme }) => ($ativo ? theme.cores.primary : theme.cores.card)};
  color: ${({ $ativo, theme }) => ($ativo ? theme.cores.inkInverse : theme.cores.inkSecondary)};
  font-size: ${({ theme }) => theme.fonte.small};
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.cores.primary};
  }

  ${media.mobile} {
    height: ${({ theme }) => theme.alturaControle.md};
  }
`;

export const Resumo = styled.button`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px ${({ theme }) => theme.espaco.md};
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  font-size: ${({ theme }) => theme.fonte.small};
  color: ${({ theme }) => theme.cores.inkSecondary};
  cursor: pointer;

  .periodo {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: ${({ theme }) => theme.cores.ink};
  }

  &:hover .periodo {
    color: ${({ theme }) => theme.cores.primary};
  }
`;

// Widgets: KPIs em grade fluida; gráficos em 2 colunas no desktop, séries no tempo na linha toda
export const GradeWidgets = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.espaco.md};

  ${media.mobile} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const GradeKpis = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: ${({ theme }) => theme.espaco.md};

  ${media.mobile} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const cartao = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  background-color: ${({ theme }) => theme.cores.card};
  border: 1px solid ${({ theme }) => theme.cores.border};
  border-radius: ${({ theme }) => theme.raio.card};
`;

export const CardKpi = styled.div`
  ${cartao}
  gap: 4px;
  padding: ${({ theme }) => theme.espaco.md} ${({ theme }) => theme.espaco.lg};

  .titulo {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: ${({ theme }) => theme.fonte.caption};
    color: ${({ theme }) => theme.cores.inkSecondary};
  }

  .positivo {
    color: ${({ theme }) => theme.cores.good};
  }

  .negativo {
    color: ${({ theme }) => theme.cores.critical};
  }

  .valor {
    font-size: 22px;
    font-weight: 600;
    color: ${({ theme }) => theme.cores.ink};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .detalhe {
    font-size: ${({ theme }) => theme.fonte.caption};
    color: ${({ theme }) => theme.cores.inkMuted};
  }

  ${media.mobile} {
    padding: ${({ theme }) => theme.espaco.md};

    .valor {
      font-size: 18px;
    }
  }
`;

export const CardWidget = styled.div<{ $larguraTotal?: boolean }>`
  ${cartao}
  gap: ${({ theme }) => theme.espaco.lg};
  padding: ${({ theme }) => theme.espaco.lg};
  ${({ $larguraTotal }) => $larguraTotal && 'grid-column: 1 / -1;'}

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.espaco.sm};
    min-height: ${({ theme }) => theme.alturaControle.sm};

    h3 {
      font-size: ${({ theme }) => theme.fonte.h3};
      font-weight: 600;
      color: ${({ theme }) => theme.cores.ink};
    }
  }
`;

// Tabela dos widgets: sem moldura própria (o card já tem), rola na horizontal
export const TabelaWidget = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  table {
    min-width: 100%;
  }
`;

export const Estado = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.espaco.md};
  padding: ${({ theme }) => theme.espaco.xxl} ${({ theme }) => theme.espaco.lg};
  background-color: ${({ theme }) => theme.cores.card};
  border: 1px solid ${({ theme }) => theme.cores.border};
  border-radius: ${({ theme }) => theme.raio.card};
  text-align: center;
  color: ${({ theme }) => theme.cores.inkSecondary};

  strong {
    font-size: ${({ theme }) => theme.fonte.h3};
    color: ${({ theme }) => theme.cores.ink};
  }

  .icone {
    font-size: 22px;
    color: ${({ theme }) => theme.cores.inkMuted};
  }

  .icone.erro {
    color: ${({ theme }) => theme.cores.critical};
  }

  .girando {
    animation: girar 0.8s linear infinite;
  }

  @keyframes girar {
    to {
      transform: rotate(360deg);
    }
  }
`;

// No modal o laytel.porfic (pensado para a tela inteira) espreme os campos: a largura é fluida
export const CamposFiltro = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.espaco.md};

  && > * {
    flex: 1 1 220px;
  }
`;

// Ao trocar o filtro, os widgets anteriores ficam esmaecidos até a nova resposta chegar
export const Conteudo = styled.div<{ $atualizando: boolean }>`
  opacity: ${({ $atualizando }) => ($atualizando ? 0.45 : 1)};
  pointer-events: ${({ $atualizando }) => ($atualizando ? 'none' : 'auto')};
  transition: opacity 0.15s ease;
`;
